/**
 * MobileToolbar - Compact toolbar for mobile devices
 *
 * BYK-168: Responsive collapsing for smaller screens
 * Provides hamburger menu and essential controls for mobile.
 */

import { Menu, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFloorPlanStore } from "@/stores/floorPlan";

// ============================================================================
// Types
// ============================================================================

export interface MobileToolbarProps {
  /** Callback when left menu button is pressed */
  onOpenLeftDrawer: () => void;
  /** Callback when right menu button is pressed */
  onOpenRightDrawer: () => void;
  /** Custom class name */
  className?: string;
}

// ============================================================================
// View Mode Button
// ============================================================================

interface ViewModeButtonProps {
  mode: "2d" | "3d" | "split";
  currentMode: "2d" | "3d" | "split";
  onClick: () => void;
  children: React.ReactNode;
}

function ViewModeButton({
  mode,
  currentMode,
  onClick,
  children,
}: ViewModeButtonProps) {
  const isActive = mode === currentMode;
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
        isActive
          ? "bg-blue-600 text-white"
          : "text-gray-600 hover:bg-gray-100"
      )}
      aria-pressed={isActive}
    >
      {children}
    </button>
  );
}

// ============================================================================
// Component
// ============================================================================

export function MobileToolbar({
  onOpenLeftDrawer,
  onOpenRightDrawer,
  className,
}: MobileToolbarProps) {
  const { viewMode, setViewMode } = useFloorPlanStore();

  return (
    <div
      className={cn(
        "flex h-14 items-center justify-between px-3",
        "border-b border-gray-200 bg-white",
        className
      )}
    >
      {/* Left: Hamburger menu (44px for WCAG 2.5.5 touch target) */}
      <button
        onClick={onOpenLeftDrawer}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-lg",
          "text-gray-600 hover:bg-gray-100",
          "focus:outline-none focus:ring-2 focus:ring-blue-500"
        )}
        aria-label="Open create tools menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Center: View mode toggle */}
      <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
        <ViewModeButton
          mode="2d"
          currentMode={viewMode}
          onClick={() => setViewMode("2d")}
        >
          2D
        </ViewModeButton>
        <ViewModeButton
          mode="3d"
          currentMode={viewMode}
          onClick={() => setViewMode("3d")}
        >
          3D
        </ViewModeButton>
        <ViewModeButton
          mode="split"
          currentMode={viewMode}
          onClick={() => setViewMode("split")}
        >
          Split
        </ViewModeButton>
      </div>

      {/* Right: Quick access buttons (44px for WCAG 2.5.5 touch target) */}
      <div className="flex items-center gap-1">
        <button
          onClick={onOpenRightDrawer}
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-lg",
            "text-gray-600 hover:bg-gray-100",
            "focus:outline-none focus:ring-2 focus:ring-blue-500"
          )}
          aria-label="Open properties panel"
        >
          <Settings className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

export default MobileToolbar;
